import { useState } from "react";
import { X, Clock, Plus, Loader2, Calendar } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import Swal from "sweetalert2";
import { insertarTurnoZonaComun } from "../../../../api/zonaComunTurnosService";

interface CreateTurnoModalProps {
  isOpen: boolean;
  horarioPorDiaCodigo: number;
  nombreDia: string;
  horaApertura: string;
  horaCierre: string;
  onClose: (created: boolean) => void;
}

export default function CreateTurnoModal({ 
  isOpen, 
  horarioPorDiaCodigo,
  nombreDia,
  horaApertura,
  horaCierre,
  onClose 
}: CreateTurnoModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [horaInicio, setHoraInicio] = useState("");
  const [horaFin, setHoraFin] = useState("");

  const validateTimeFormat = (time: string): boolean => {
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    return timeRegex.test(time);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!horaInicio) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor especifique la hora de inicio",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10004';
          const container = popup.closest('.swal2-container');
          if (container) {
            (container as HTMLElement).style.zIndex = '10004';
          }
        },
      });
      return;
    }

    if (!validateTimeFormat(horaInicio)) {
      Swal.fire({
        icon: "warning",
        title: "Formato inválido",
        text: "La hora de inicio debe estar en formato militar (HH:MM). Ejemplo: 13:00",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10004';
          const container = popup.closest('.swal2-container');
          if (container) {
            (container as HTMLElement).style.zIndex = '10004';
          }
        },
      });
      return;
    }

    if (!horaFin) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor especifique la hora de finalización",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10004';
          const container = popup.closest('.swal2-container');
          if (container) {
            (container as HTMLElement).style.zIndex = '10004';
          }
        },
      });
      return;
    }

    if (!validateTimeFormat(horaFin)) {
      Swal.fire({
        icon: "warning",
        title: "Formato inválido",
        text: "La hora de finalización debe estar en formato militar (HH:MM). Ejemplo: 15:00",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10004';
          const container = popup.closest('.swal2-container');
          if (container) {
            (container as HTMLElement).style.zIndex = '10004';
          }
        },
      });
      return;
    }

    const [inicioHora, inicioMinuto] = horaInicio.split(':').map(Number);
    const [finHora, finMinuto] = horaFin.split(':').map(Number);
    const inicioTotal = inicioHora * 60 + inicioMinuto;
    const finTotal = finHora * 60 + finMinuto;

    if (finTotal <= inicioTotal) {
      Swal.fire({
        icon: "warning",
        title: "Horario inválido",
        text: "La hora de finalización debe ser posterior a la hora de inicio",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10004';
          const container = popup.closest('.swal2-container');
          if (container) {
            (container as HTMLElement).style.zIndex = '10004';
          }
        },
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        "zona comun horario por dia codigo": horarioPorDiaCodigo,
        "inicio turno": horaInicio,
        "fin turno": horaFin,
      };

      const response = await insertarTurnoZonaComun(payload);

      if (response.code === "1" || response.code === 1) {
        Swal.fire({
          icon: "success",
          title: "¡Turno agregado!",
          text: response.message || "El turno se ha agregado exitosamente",
          confirmButtonColor: "#3d5a40",
          timer: 2000,
          timerProgressBar: true,
          customClass: {
            popup: 'z-[10002]',
            container: 'z-[10002]'
          },
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10002';
            const container = popup.closest('.swal2-container');
            if (container) {
              (container as HTMLElement).style.zIndex = '10002';
            }
          },
        });
        onClose(true);
      } else {
        throw new Error(response.message || "Error al agregar el turno");
      }
    } catch (error) {
      console.error("Error al crear turno:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al agregar el turno";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10004';
          const container = popup.closest('.swal2-container');
          if (container) {
            (container as HTMLElement).style.zIndex = '10004';
          }
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10003] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) {
          onClose(false);
        }
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[95vh] flex flex-col">
        <div className="sticky top-0 bg-gradient-to-r from-military-500 to-military-600 text-white px-6 py-4 rounded-t-xl flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Plus size={28} />
            Agregar Turno por Día
          </h2>
          <button
            onClick={() => onClose(false)}
            className="text-white hover:bg-white/20 rounded-full p-1 transition"
            disabled={isSubmitting}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Información del día y horarios */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar className="text-gray-500" size={18} />
                <span className="text-sm font-semibold text-gray-700">Creando turno para: {nombreDia}</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-gray-500" />
                  <span className="text-gray-600">Apertura:</span>
                  <span className="font-medium text-gray-800">{horaApertura}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-gray-500" />
                  <span className="text-gray-600">Cierre:</span>
                  <span className="font-medium text-gray-800">{horaCierre}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl border-2 border-gray-200 p-5 shadow-sm">
            <label className="block text-base font-bold text-gray-800 mb-3">
              <Clock size={18} className="inline mr-2 text-military-600" />
              Hora de Inicio (Formato Militar) *
            </label>
            <Input
              type="text"
              value={horaInicio}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || /^([01]?\d|2[0-3]):?([0-5]?\d?)$/.test(value)) {
                  if (value.length === 2 && !value.includes(':')) {
                    setHoraInicio(value + ':');
                  } else {
                    setHoraInicio(value);
                  }
                }
              }}
              placeholder="13:00"
              disabled={isSubmitting}
              className="w-full p-3.5 shadow-sm border-2 focus:border-military-500 rounded-lg text-base"
              maxLength={5}
              required
            />
            <p className="text-xs text-gray-500 mt-2">
              Formato: HH:MM (24 horas). Ejemplo: 13:00, 14:30
            </p>
          </div>

          <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl border-2 border-gray-200 p-5 shadow-sm">
            <label className="block text-base font-bold text-gray-800 mb-3">
              <Clock size={18} className="inline mr-2 text-military-600" />
              Hora de Finalización (Formato Militar) *
            </label>
            <Input
              type="text"
              value={horaFin}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || /^([01]?\d|2[0-3]):?([0-5]?\d?)$/.test(value)) {
                  if (value.length === 2 && !value.includes(':')) {
                    setHoraFin(value + ':');
                  } else {
                    setHoraFin(value);
                  }
                }
              }}
              placeholder="15:00"
              disabled={isSubmitting}
              className="w-full p-3.5 shadow-sm border-2 focus:border-military-500 rounded-lg text-base"
              maxLength={5}
              required
            />
            <p className="text-xs text-gray-500 mt-2">
              Formato: HH:MM (24 horas). Ejemplo: 15:00, 17:30
            </p>
          </div>

          <div className="sticky bottom-0 bg-white border-t pt-4 mt-6 -mx-6 px-6 pb-6">
            <div className="max-w-2xl mx-auto flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onClose(false)}
                disabled={isSubmitting}
                className="flex-1 border-2 hover:bg-gray-50"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-military-500 hover:bg-military-600 text-white shadow-lg font-semibold flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Plus size={20} />
                    Guardar Turno
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

